import React from 'react';
import { Provider } from 'react-redux';
import { Store } from 'redux';
import { BrowserRouter } from 'react-router-dom';
import {
    TranslateProvider,
    TranslateScope,
    Translations,
    LanguageCode,
} from '@pegasus/translation';
import { CommercialMessagesProvider } from '@pegasus/commercial-messages';
import { ErrorBoundary, ErrorMessageFullScreen } from '@browsing/components';
import { ConfigProvider, Config } from '@browsing/config';

import { getIsLoggedIn } from '@client/redux/modules/customer';
import MakedRoutes from '@client/routes';
import { LUXProviderWrapper } from './lux-provider-wrapper';

interface AppRootProps {
    store: Store;
    translations: Translations;
    config: Config;
}

const getLanguageCode = (config: Config): LanguageCode => {
    try {
        const locale = config?.locale?.[config.appdomain];
        if (!locale) {
            return 'de-DE' as LanguageCode;
        }
        return locale.replace('_', '-') as LanguageCode;
    } catch {
        return 'de-DE' as LanguageCode;
    }
};

const ErrorFallback = () => <ErrorMessageFullScreen path="error" withCta={false} />;

export default function AppRoot({ store, translations, config }: AppRootProps) {
    const state = store.getState();
    const isLoggedIn = getIsLoggedIn(state);
    const languageCode = getLanguageCode(config);

    return (
        <ConfigProvider config={config}>
            <TranslateProvider translations={translations} locale={languageCode}>
                <TranslateScope>
                    <Provider store={store}>
                        <LUXProviderWrapper>
                            <ErrorBoundary fallbackUI={ErrorFallback} componentName="AppRoot">
                                <CommercialMessagesProvider localeCode={config.localeCode}>
                                    <BrowserRouter>
                                        <MakedRoutes isLoggedIn={isLoggedIn} />
                                    </BrowserRouter>
                                </CommercialMessagesProvider>
                            </ErrorBoundary>
                        </LUXProviderWrapper>
                    </Provider>
                </TranslateScope>
            </TranslateProvider>
        </ConfigProvider>
    );
}
